// ~/Aetheris/frontend/src/components/Nextcloud/NextcloudPanel.jsx

import React, { useState, useEffect, useCallback } from 'react';

const MANAGEMENT_ROLES = new Set([
  'manager', 'admin', 'superadmin', 'ceo', 'chairman',
  'division_manager', 'hr_director', 'finance', 'it_manager',
]);

const FOLDER_ICONS = {
  Reports:    '📋',
  Employees:  '👥',
  Clients:    '🤝',
  Policies:   '📜',
  Shared:     '🔗',
  Admin:      '🔐',
  Management: '🏢',
};

function getToken() {
  return localStorage.getItem('accessToken') || '';
}

function authHeaders() {
  return { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' };
}

async function apiFetch(path, opts = {}) {
  if (opts.body && !opts.headers) {
    opts.headers = { 'Content-Type': 'application/json' };
  }
  const res = await fetch(`/api/nextcloud${path}`, opts);
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || res.statusText);
  }
  return res.json();
}

// ── FileRow ────────────────────────────────────────────────────────────────

function FileRow({ item, onNavigate, onDelete }) {
  const icon = item.isDirectory
    ? (FOLDER_ICONS[item.name] || '📁')
    : fileIcon(item.contentType, item.name);

  const size = item.isDirectory ? '' : formatSize(item.size);
  const mod  = item.lastModified ? new Date(item.lastModified).toLocaleDateString() : '';

  return (
    <div style={styles.fileRow}>
      <span style={styles.fileIcon}>{icon}</span>
      <span
        style={{ ...styles.fileName, cursor: item.isDirectory ? 'pointer' : 'default' }}
        onClick={() => item.isDirectory && onNavigate(item.name)}
      >
        {item.name}
      </span>
      <span style={styles.fileMeta}>{size}</span>
      <span style={styles.fileMeta}>{mod}</span>
      <button style={styles.deleteBtn} onClick={() => onDelete(item)} title="Delete">✕</button>
    </div>
  );
}

function fileIcon(mimeType, name) {
  if (!mimeType && !name) return '📄';
  const ext = (name || '').split('.').pop().toLowerCase();
  if (['jpg','jpeg','png','gif','webp'].includes(ext)) return '🖼️';
  if (['pdf'].includes(ext)) return '📕';
  if (['json'].includes(ext)) return '{}';
  if (['xlsx','xls','csv'].includes(ext)) return '📊';
  if (['docx','doc'].includes(ext)) return '📝';
  return '📄';
}

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ── main component ─────────────────────────────────────────────────────────

export default function NextcloudPanel({ user }) {
  const role = user?.role || user?.userRole || '';

  if (!MANAGEMENT_ROLES.has(role)) {
    return (
      <div style={styles.denied}>
        <span style={{ fontSize: 32 }}>🔒</span>
        <p>Access restricted to management roles.</p>
      </div>
    );
  }

  const [status,    setStatus]    = useState(null);   // { connected, folders }
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');
  const [path,      setPath]      = useState('');     // current folder path
  const [items,     setItems]     = useState([]);
  const [browsing,  setBrowsing]  = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newFolder, setNewFolder] = useState('');
  const [showNew,   setShowNew]   = useState(false);

  // ── init: test connection + ensure folders ─────────────────────────────

  useEffect(() => {
    apiFetch('/status')
      .then(data => {
        setStatus(data);
        setLoading(false);
        if (data.connected) loadFolder('');
      })
      .catch(err => { setError(err.message); setLoading(false); });
  }, []);

  // ── folder navigation ──────────────────────────────────────────────────

  const loadFolder = useCallback(async (folderPath) => {
    setBrowsing(true);
    setError('');
    try {
      const data = await apiFetch(`/files?path=${encodeURIComponent(folderPath)}`);
      setItems(data.items || []);
      setPath(folderPath);
    } catch (err) {
      setError(err.message);
    } finally {
      setBrowsing(false);
    }
  }, []);

  function navigate(subFolder) {
    const next = path ? `${path}/${subFolder}` : subFolder;
    loadFolder(next);
  }

  function goUp() {
    const parts = path.split('/').filter(Boolean);
    parts.pop();
    loadFolder(parts.join('/'));
  }

  // ── upload ─────────────────────────────────────────────────────────────

  async function handleUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const buf    = await file.arrayBuffer();
      const b64    = btoa(String.fromCharCode(...new Uint8Array(buf)));
      const remote = path ? `${path}/${file.name}` : file.name;
      await apiFetch('/upload', {
        method: 'POST',
        body: JSON.stringify({ path: remote, content: b64, mimeType: file.type }),
      });
      await loadFolder(path);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  }

  // ── create folder ──────────────────────────────────────────────────────

  async function handleCreateFolder() {
    if (!newFolder.trim()) return;
    const fp = path ? `${path}/${newFolder.trim()}` : newFolder.trim();
    try {
      await apiFetch('/folder', { method: 'POST', body: JSON.stringify({ path: fp }) });
      setNewFolder('');
      setShowNew(false);
      await loadFolder(path);
    } catch (err) {
      setError(err.message);
    }
  }

  // ── delete ─────────────────────────────────────────────────────────────

  async function handleDelete(item) {
    if (!window.confirm(`Delete "${item.name}"?`)) return;
    try {
      const fp = path ? `${path}/${item.name}` : item.name;
      const suffix = item.isDirectory ? '/' : '';
      await apiFetch(`/file?path=${encodeURIComponent(fp + suffix)}`, { method: 'DELETE' });
      await loadFolder(path);
    } catch (err) {
      setError(err.message);
    }
  }

  // ── render ─────────────────────────────────────────────────────────────

  if (loading) return <div style={styles.center}>Connecting to Nextcloud…</div>;

  if (!status?.connected) {
    return (
      <div style={styles.denied}>
        <span style={{ fontSize: 32 }}>⚠️</span>
        <p>Could not connect to Nextcloud.</p>
        <p style={{ fontSize: 12, color: '#94a3b8' }}>{error || 'Check your VPN / Tailscale connection.'}</p>
      </div>
    );
  }

  const breadcrumbs = path ? path.split('/').filter(Boolean) : [];

  return (
    <div style={styles.container}>
      {/* header */}
      <div style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 36 }}>☁️</span>
          <div>
            <div style={styles.title}>Nextcloud Files</div>
            <div style={styles.subtitle}>
              {Object.keys(status.folders || {}).length} folders active
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <label style={styles.uploadBtn}>
            {uploading ? 'Uploading…' : '⬆ Upload'}
            <input type="file" style={{ display: 'none' }} onChange={handleUpload} disabled={uploading} />
          </label>
          <button style={styles.actionBtn} onClick={() => setShowNew(v => !v)}>+ Folder</button>
        </div>
      </div>

      {error && <div style={styles.errorBanner}>{error}</div>}

      {/* new folder input */}
      {showNew && (
        <div style={styles.newFolderRow}>
          <input
            style={styles.input}
            placeholder="Folder name"
            value={newFolder}
            onChange={e => setNewFolder(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleCreateFolder()}
            autoFocus
          />
          <button style={styles.actionBtn} onClick={handleCreateFolder}>Create</button>
          <button style={styles.ghostBtn} onClick={() => { setShowNew(false); setNewFolder(''); }}>Cancel</button>
        </div>
      )}

      {/* breadcrumb */}
      <div style={styles.breadcrumb}>
        <span
          style={{ cursor: 'pointer', color: '#60a5fa' }}
          onClick={() => loadFolder('')}
        >Root</span>
        {breadcrumbs.map((crumb, i) => (
          <React.Fragment key={i}>
            <span style={{ color: '#475569' }}> / </span>
            <span
              style={{ cursor: 'pointer', color: i === breadcrumbs.length - 1 ? '#e2e8f0' : '#60a5fa' }}
              onClick={() => loadFolder(breadcrumbs.slice(0, i + 1).join('/'))}
            >{crumb}</span>
          </React.Fragment>
        ))}
      </div>

      {/* back button */}
      {path && (
        <button style={styles.backBtn} onClick={goUp}>← Back</button>
      )}

      {/* file list */}
      <div style={styles.fileList}>
        {browsing && <div style={styles.center}>Loading…</div>}
        {!browsing && items.length === 0 && (
          <div style={styles.empty}>This folder is empty.</div>
        )}
        {!browsing && items.map((item, i) => (
          <FileRow key={i} item={item} onNavigate={navigate} onDelete={handleDelete} />
        ))}
      </div>

      {/* folder status badges */}
      {!path && status.folders && (
        <div style={styles.folderStatus}>
          {Object.entries(status.folders).map(([name, state]) => (
            <span key={name} style={{ ...styles.badge, background: state === 'created' ? '#16a34a22' : '#1e293b' }}>
              {FOLDER_ICONS[name] || '📁'} {name}
              {state === 'created' && <span style={{ color: '#4ade80', marginLeft: 4 }}>✓ new</span>}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ── styles ─────────────────────────────────────────────────────────────────

const styles = {
  container: {
    display: 'flex', flexDirection: 'column', gap: 12,
    padding: 20, color: '#e2e8f0', fontFamily: 'inherit', minHeight: '100%',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    borderBottom: '1px solid rgba(59,130,246,0.2)', paddingBottom: 12,
  },
  title:    { color: '#3b82f6', fontWeight: 700, fontSize: 18 },
  subtitle: { color: '#64748b', fontSize: 12 },
  breadcrumb: {
    fontSize: 13, color: '#94a3b8',
    background: 'rgba(255,255,255,0.03)', borderRadius: 6,
    padding: '6px 10px',
  },
  fileList: { flex: 1, display: 'flex', flexDirection: 'column', gap: 2 },
  fileRow: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '8px 10px', borderRadius: 6,
    background: 'rgba(255,255,255,0.03)',
    transition: 'background 0.15s',
  },
  fileIcon:  { fontSize: 18, minWidth: 24, textAlign: 'center' },
  fileName:  { flex: 1, fontSize: 14, color: '#e2e8f0', wordBreak: 'break-all' },
  fileMeta:  { fontSize: 12, color: '#64748b', minWidth: 60, textAlign: 'right' },
  deleteBtn: {
    background: 'none', border: 'none', color: '#ef444466', cursor: 'pointer',
    fontSize: 14, padding: '2px 6px', borderRadius: 4,
  },
  uploadBtn: {
    background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.3)',
    color: '#60a5fa', borderRadius: 6, padding: '6px 14px', fontSize: 13,
    cursor: 'pointer', display: 'inline-block',
  },
  actionBtn: {
    background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.3)',
    color: '#60a5fa', borderRadius: 6, padding: '6px 14px', fontSize: 13, cursor: 'pointer',
  },
  ghostBtn: {
    background: 'none', border: '1px solid #334155',
    color: '#64748b', borderRadius: 6, padding: '6px 14px', fontSize: 13, cursor: 'pointer',
  },
  backBtn: {
    background: 'none', border: 'none', color: '#60a5fa',
    cursor: 'pointer', fontSize: 13, padding: '4px 0', textAlign: 'left',
  },
  newFolderRow: { display: 'flex', gap: 8, alignItems: 'center' },
  input: {
    flex: 1, background: '#0f172a', border: '1px solid #334155',
    borderRadius: 6, color: '#e2e8f0', padding: '6px 10px', fontSize: 14,
  },
  empty: { color: '#475569', fontSize: 13, padding: '20px 0', textAlign: 'center' },
  center: { textAlign: 'center', color: '#64748b', padding: 20 },
  denied: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', gap: 8, color: '#64748b', padding: 40,
  },
  errorBanner: {
    background: '#7f1d1d33', border: '1px solid #ef444433',
    color: '#fca5a5', borderRadius: 6, padding: '8px 12px', fontSize: 13,
  },
  folderStatus: { display: 'flex', flexWrap: 'wrap', gap: 8, paddingTop: 8 },
  badge: {
    borderRadius: 20, padding: '4px 12px', fontSize: 12,
    border: '1px solid #1e293b', color: '#94a3b8',
  },
};
